import fApp from 'firebase/app';
import 'firebase/auth';
import axios from 'axios';
import mime from 'mime';
import { isProductionEnv, TwoFactorAuthErrorString } from './helpers';

const BASE_URL = isProductionEnv()
    ? 'https://business.pressenger.com/pressenger-admin/backings'
    : 'https://test.pressenger.com/pressenger-admin/backings';

export const firebase = fApp.initializeApp({
    apiKey: "AIzaSyCIWr267i5LGzYS76pq572GQIOsUydkZyU",
    authDomain: "pressenger-thebojda.firebaseapp.com",
    databaseURL: "https://pressenger-thebojda.firebaseio.com",
    projectId: "pressenger-thebojda",
    storageBucket: "pressenger-thebojda.appspot.com",
    messagingSenderId: "260008251190",
    appId: "1:260008251190:web:d871d9e42b02e93a5a2d97",
});

export const fbFacebookProvider = new fApp.auth.FacebookAuthProvider();

export const fbGoogleProvider = new fApp.auth.GoogleAuthProvider();

firebase.auth().signOut();

const www = axios.create({
    baseURL: BASE_URL,
    timeout: 25000,
});

let currentCsrf = localStorage.getItem('csrf') || null;
let unauthorizedExceptionFn = null;
let recipientList = localStorage.getItem('recipientList') || null;
if (recipientList === 'null') {
    recipientList = null;
}

www.interceptors.response.use(response => {
    if (!unauthorizedExceptionFn instanceof Function) {
        return response;
    }
    const data = response.data;
    if (data.className && data.className === 'UnauthorizedException') {
        unauthorizedExceptionFn();
    }
    return response;
}, error => Promise.reject(error));

export const setUnauthorizedExceptionFn = fn => {
    unauthorizedExceptionFn = fn;
};

export const execute = (service, action, payload = {}, opts = {}) => {
    return new Promise((resolve, reject) => {
        const url = `/${service}/${action}`;
        const data = new FormData;
        let dlen = 0;
        for (let [key, value] of Object.entries(payload)) {
            if (value instanceof File) {
                data.append(key, value, value.name);
            } else if (value instanceof Blob) {
                data.append(key, value, `file.${mime.getExtension(value.type)}`);
            } else if (typeof value === 'object') {
                data.append(key, JSON.stringify(value));
            } else {
                data.append(key, value);
            }
            dlen++;
        }
        let rd = dlen ? data : undefined;
        if (opts.doJSON) {
            rd = payload;
        }
        www.post(url, rd, {
            withCredentials: true,
            responseType: opts.responseType || 'json',
            headers: {
                Accept: 'text/html',
                'CSRF-Token': currentCsrf,
            },
        }).then(response => {
            resolve(response.data);
        }).catch(error => {
            reject(error);
        });
    });
};

export const getCsrf = () => currentCsrf;

export const accessManagement = {
    login(email, password) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'login', {
                email,
                password,
            }, {
                csrfOff: true,
                csrfRefresh: true,
            }).then(response => {
                if (response.result) {
                    resolve();
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    loginEx(email, password) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'loginEx', {
                email,
                password,
            }).then(response => {
                if (response.result) {
                    if (response.result === TwoFactorAuthErrorString) {
                        reject(TwoFactorAuthErrorString);
                    } else {
                        currentCsrf = response.result;
                        localStorage.setItem('csrf', currentCsrf);
                        resolve();
                    }
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    loginWithGA(email, password, code) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'loginWithGA', {
                email,
                password,
                code,
            }).then(response => {
                if (response.result) {
                    currentCsrf = response.result;
                    localStorage.setItem('csrf', currentCsrf);
                    resolve();
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    loginWithTokenEx(token) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'loginWithTokenEx', {
                token,
            }).then(response => {
                if (response.result) {
                    currentCsrf = response.result;
                    localStorage.setItem('csrf', currentCsrf);
                    resolve();
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    loginWithFA(faToken) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'loginWithFA', {
                faToken,
            }).then(response => {
                if (response.result) {
                    currentCsrf = response.result;
                    localStorage.setItem('csrf', currentCsrf);
                    resolve();
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    logout() {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'logout').then(() => {
                currentCsrf = null;
                if (firebase.auth().currentUser) {
                    firebase.auth().signOut();
                }
                localStorage.setItem('csrf', currentCsrf);
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },
    changePassword(oldPassword, newPassword, newPasswordAgain) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'changePassword', {
                oldPassword,
                newPassword,
                newPasswordAgain,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    changePasswordWithToken(token, newPassword) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'changePasswordWithToken', {
                token,
                newPassword,
            }).then(response => {
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },
    changeFATokenWithToken(token, faToken) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'changeFATokenWithToken', {
                token,
                faToken,
            }).then(response => {
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },
    getSessionId() {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'getSessionId').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    loginWithJWTToken(token, provider) {
        return new Promise((resolve, reject) => {
            execute('accessManagement', 'loginWithJWTToken', {
                token,
                provider,
            }).then(response => {
                if (response.result) {
                    currentCsrf = response.result;
                    localStorage.setItem('csrf', currentCsrf);
                    resolve();
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
};

export const pressengerControl = {
    getUserInfo() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getUserInfo').then(response => {
                if (response.result) {
                    resolve(response.result);
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    getNotificationLogos(gid) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getNotificationLogos', {
                gid,
            }).then(response => {
                if (response.result) {
                    resolve(response.result);
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    getTransitions(gid) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getTransitions', {
                gid,
            }).then(response => {
                if (response.result) {
                    resolve(response.result);
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    listBrandPics() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'listBrandPics', undefined).then(response => {
                if (response.result) {
                    resolve(response.result);
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    sendMassPressage(options) {
        const opts = Object.assign({
            name: '',
            sender: '',
            text: '',
            image: '',
            phoneNumbers: '',
            type: '',
            htmlTemplateName: '',
            pos: '0',
            link: '',
            sendToAll: 'true',
            bannerId: 0,
            sendDate: '',
            logoName: '',
            notificationImageName: '',
            advertisementImageName: '',
            extendedNotificationImageName: '',
            iOSLogoName: '',
            iOSSecondImageName: '',
            iOSWatchImageName: '',
            disableBubble: false,
            message: '',
            channelId: '',
        }, options);
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'sendMassPressage', opts, {forcePost: true}).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    generatePressage(options) {
        const opts = Object.assign({
            name: '',
            sender: '',
            text: '',
            image: '',
            phoneNumbers: '',
            type: '',
            htmlTemplateName: '',
            pos: '0',
            link: '',
            bannerId: 0,
            logoName: '',
            notificationImageName: '',
            advertisementImageName: '',
            extendedNotificationImageName: '',
            iOSLogoName: '',
            iOSSecondImageName: '',
            iOSWatchImageName: '',
            disableBubble: false,
            message: '',
        }, options);
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'generatePressage', opts, {forcePost: true}).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteNotificationLogo(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteNotificationLogo', {
                fileName,
            }).then(() => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteTransition(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteTransition', {
                fileName,
            }).then(() => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteBrandPicById(id) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteBrandPicById', {
                id,
            }).then(() => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setDefaultLogo(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setDefaultLogo', {
                fileName,
            }).then(() => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setDefaultTransition(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setDefaultTransition', {
                fileName,
            }).then(() => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
    // TODO: This looks super weird
    uploadNotificationLogo(logo) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadNotificationLogo', {
                logo,
            }).then(response => {
                response.result || response.result === null ? resolve(response.result) : reject();
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadTransition(transition) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadTransition', {
                transition,
            }).then(response => {
                response.result || response.result === null ? resolve(response.result) : reject();
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadBrandPic(brandPic, link, title) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadBrandPic', {
                brandPic,
                link,
                title,
            }).then(response => {
                response.result || response.result === null ? resolve(response.result) : reject();
            }).catch(error => {
                reject(error);
            });
        });
    },
    setBrandPicLinkById(id, link) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setBrandPicLinkById', {
                id,
                link,
            }).then(() => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
    listPressages(sent = true, limit = 2, offset = 0) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'listPressages', {
                sent,
                limit,
                offset,
            }).then(response => {
                resolve(response.result.map(i => Object.assign(i, {_sent: sent})));
            }).catch(error => {
                reject(error);
            });
        });
    },
    listNotifyLogoStat() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'listNotifyLogoStat').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    listTransitionStat() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'listTransitionStat').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getChannelList(gid) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getChannelList', {
                gid,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getMyEditorsList() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getMyEditorsList').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getMassPressageLogCSV(pressageId) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getMassPressageLogCSV', {
                pressageId,
            }, {
                responseType: 'blob',
            }).then(response => {
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },
    removeMassPressage(pressageId) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'removeMassPressage', {
                pressageId,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setChannelData(logo, name, description) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setChannelData', {
                logo: logo || '',
                name,
                description,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setWelcomeUrl(url) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setWelcomeUrl', {
                url: url || '',
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setWelcomePressage(image) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setWelcomePressage', {
                image,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    disableUser(userId) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'disableUser', {
                userId,
            }, {
                doJSON: true,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    registerUserDtoWithParent(dto) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'registerUserDtoWithParent', {
                dto,
            }).then(response => {
                if (response.result && response.result.successful) {
                    resolve(response.result);
                } else {
                    reject();
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    savePushIds(apc, apc_pass, fcm_key) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'savePushIds', {
                apc,
                apc_pass,
                fcm_key,
            }).then(response => {
                response.result ? resolve(response.result) : reject();
                resolve(response);
            }).catch(error => {
                reject(error);
            })
        });
    },
    getUsersCSV() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getUsersCSV', {}, {
                responseType: 'blob',
            }).then(response => {
                resolve(response);
            }).catch(error => {
                reject(error);
            })
        });
    },
    getSenderGroupId() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getSenderGroupId').then(response => {
                response.result ? resolve(response.result) : reject();
                resolve(response);
            }).catch(error => {
                reject(error);
            })
        });
    },
    uploadNotificationLogoFallback(file, logoName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadNotificationLogoFallback', {
                fallback: file,
                logoName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadNotificationIOSLogo(file, logoName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadNotificationIOSLogo', {
                ios_logo: file,
                logoName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadNotificationLogoFallbackBanner(file, logoName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadNotificationLogoFallbackBanner', {
                fallbackBanner: file,
                logoName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setExtraPushUrl(url) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setExtraPushUrl', {
                url,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadNotificationImage(image, temporary = false) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadNotificationImage', {
                image,
                temporary,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getNotificationImages() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getNotificationImages').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteNotificationImage(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteNotificationImage', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadAdvertisementImage(image, temporary = false) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadAdvertisementImage', {
                image,
                //temporary,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getAdvertisementImages() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getAdvertisementImages').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteAdvertisementImage(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteAdvertisementImage', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadExtendedNotificationImage(image, temporary = false) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadExtendedNotificationImage', {
                image,
                temporary,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getExtendedNotificationImages() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getExtendedNotificationImages').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteExtendedNotificationImage(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteExtendedNotificationImage', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadiOSLogo(image, temporary = false) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadiOSLogo', {
                image,
                temporary,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getiOSLogos() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getiOSLogos').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteiOSLogo(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteiOSLogo', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadiOSWatchImage(image, temporary = false) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadiOSWatchImage', {
                image,
                temporary,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getiOSWatchImages() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getiOSWatchImages').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteiOSWatchImage(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteiOSWatchImage', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setLocale(locale) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setLocale', {
                locale,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getStudioImages() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getStudioImages').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadStudioImage(image, temporary = false) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadStudioImage', {
                image,
                temporary,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteStudioImage(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'deleteStudioImage', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    setWelcomePressageId(pressageId) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'setWelcomePressageId', {
                pressageId,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getPressageJson(pressageId) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getPressageJson', {
                pressageId,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getWelcomePressage() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getWelcomePressage').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getCustomMenu() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getCustomMenu').then(response => {
                resolve(response.result.elements);
            }).catch(error => {
                reject(error);
            });
        });
    },
    copyiOSLogoAsWatchImage(fileName) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'copyiOSLogoAsWatchImage', {
                fileName,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    getSessionId() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'getSessionId').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    enableGA() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'enableGA').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    disableGA() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'disableGA').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    isGAEnabled() {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'isGAEnabled').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    uploadServiceAccount(serviceAccount) {
        return new Promise((resolve, reject) => {
            execute('pressengerControl', 'uploadServiceAccount', {
                serviceAccount,
            }).then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
};

export const userStorageService = {
    getValue(key) {
        return new Promise((resolve, reject) => {
            execute('userStorageService', 'getValue', {
                key,
            }).then(response => {
                if (response.result) {
                    try {
                        const obj = JSON.parse(response.result);
                        resolve(obj);
                    } catch (ex) {
                        resolve(response.result);
                    }
                } else {
                    resolve(null);
                }
            }).catch(error => {
                reject(error);
            });
        });
    },
    setValue(key, value) {
        return new Promise((resolve, reject) => {
            execute('userStorageService', 'setValue', {
                key,
                value,
            }).then(response => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
};

export const pressageService = {
    createEntity() {
        return new Promise((resolve, reject) => {
            execute('pressageService', 'createEntity').then(response => {
                response.result ? resolve(response.result) : reject();
            }).catch(error => {
                reject(error);
            });
        });
    },
    update(id, source, name = '', published = true) {
        return new Promise((resolve, reject) => {
            execute('pressageService', 'update', {
                entity: {
                    id,
                    source: JSON.stringify(source),
                    name,
                    published,
                },
            }).then(response => {
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },
    query(name = '') {
        return new Promise((resolve, reject) => {
            execute('pressageService', 'query', {
                name,
            }).then(response => {
                const pressages = response.result.map(p => {
                    let source = {type: 'bundle'};
                    try {
                        source = JSON.parse(p.source);
                    } catch (e) {};
                    return Object.assign({}, p, {
                        source,
                    });
                });
                pressages ? resolve(pressages) : reject();
            }).catch(error => {
                reject(error);
            });
        });
    },
    findById(id) {
        return new Promise((resolve, reject) => {
            execute('pressageService', 'findById', {
                id,
            }).then(response => {
                try {
                    response.result.source = JSON.parse(response.result.source);
                    resolve(result);
                } catch (e) {
                    reject();
                }
                const { result } = response.content;
            }).catch(error => {
                reject(error);
            });
        });
    },
    deleteById(id) {
        return new Promise((resolve, reject) => {
            execute('pressageService', 'deleteById', {
                id,
            }).then(response => {
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },
};

export const imageService = {
    query() {
        return new Promise((resolve, reject) => {
            execute('imageService', 'query').then(response => {
                resolve(response.result);
            }).catch(error => {
                reject(error);
            });
        });
    },
    upload(image) {
        return new Promise((resolve, reject) => {
            execute('imageService', 'upload', {
                image,
            }).then(response => {
                response.result ? resolve(response.result) : reject();
            }).catch(error => {
                reject(error);
            })
        });
    },
    deleteById(id) {
        return new Promise((resolve, reject) => {
            execute('imageService', 'deleteById', {
                id,
            }).then(response => {
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        });
    },
};


export const getBubbleImage = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/${fileName}`;
};

export const getBubbleBannerImage = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}_fallback/fallback_${fileName}.png`;
};

export const getBubbleWatchImage = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/${fileName}.png`;
};

export const getBubbleAdCubeImage= (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}_fallback/fallback_banner_${fileName}.png`;
};

export const getTransitionImage = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}_trans/${fileName}`;
};

export const getChannelLogo = (groupId) => {
    return `https://s3.dualstack.eu-central-1.amazonaws.com/welcome-messages/${groupId}_channel.jpg`;
};

export const getPressageThumbUrl = (pressage) => {
    return `${BASE_URL}/pressengerControl/getPressagePreview?pressageId=${pressage.pressageId}`;
};

export const setRecipientList = (list) => {
    recipientList = list;
    localStorage.setItem('recipientList', recipientList);
};

export const getRecipientList = () => recipientList;

export const getNotificationImageURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_notiimage_${fileName}`;
};

export const getAdvertisementImageBigURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_notibg_${fileName}`;
};

export const getAdvertisementImageURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_advimage_${fileName}`;
};

export const getExtendedBannerURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_extnotiimage_${fileName}`;
};

export const getIOSExtendedNotificationURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_ioslogo_${fileName}`;
};

export const getIOSAppleWatchURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_ioswi_${fileName}`;
};

export const getStudioImageURL = (groupId, fileName) => {
    return `https://s3-us-west-2.amazonaws.com/brand-pics/${groupId}/_si_${fileName}`;
};
